import en from './locales/en.js';
import fi from './locales/fi.js';

/**
 * Minimal i18n runtime.
 *
 * Catalogues are flat objects of dotted keys. A value is either a string with
 * `{placeholders}`, or an object of plural forms (`one` / `other`) selected by a
 * `count` parameter. English is the fallback for anything a catalogue is missing,
 * so a partial translation degrades to English rather than showing a raw key.
 */

export const CATALOGUES = { en, fi };

export const LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'fi', name: 'Suomi' },
];

const STORAGE_KEY = 'webchat.locale';

/** Maps anything browser-shaped ('fi-FI', 'FI') onto a supported code. */
export function normalizeLocale(input) {
  const tag = String(input ?? '')
    .trim()
    .toLowerCase();
  if (!tag) return null;
  const base = tag.split(/[-_]/)[0];
  return CATALOGUES[base] ? base : null;
}

/** Stored choice first, then the browser's languages, then English. */
function detectLocale() {
  try {
    const stored = normalizeLocale(localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch {
    /* storage unavailable */
  }
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const match = normalizeLocale(candidate);
    if (match) return match;
  }
  return 'en';
}

let locale = detectLocale();
const listeners = new Set();

export function getLocale() {
  return locale;
}

/** Changes the language, persists it, and notifies subscribers to re-render. */
export function setLocale(code) {
  const next = normalizeLocale(code) ?? 'en';
  if (next === locale) return locale;
  locale = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  document.documentElement.lang = next;
  for (const listener of listeners) {
    try {
      listener(next);
    } catch (err) {
      console.error('locale listener failed', err);
    }
  }
  return locale;
}

export function onLocaleChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function lookup(code, key) {
  return CATALOGUES[code]?.[key];
}

function interpolate(template, params) {
  return String(template).replace(/\{(\w+)\}/g, (whole, name) =>
    params?.[name] === undefined || params[name] === null ? whole : String(params[name]),
  );
}

/**
 * Translates `key`. `params.count` selects a plural form when the entry has one.
 * Unknown keys fall back to English, then to the key itself, so a missing string
 * is visible in development without breaking the page.
 */
export function t(key, params = {}) {
  let entry = lookup(locale, key);
  if (entry === undefined) entry = lookup('en', key);
  if (entry === undefined) return key;

  if (typeof entry === 'object') {
    const count = Number(params.count);
    entry = (count === 1 ? entry.one : entry.other) ?? entry.other ?? entry.one;
  }
  return interpolate(entry, params);
}

/** True when a key exists in the active locale or in English. */
export function has(key) {
  return lookup(locale, key) !== undefined || lookup('en', key) !== undefined;
}

/**
 * Translates static markup in place.
 *
 *   data-i18n="ui.send"                  -> textContent
 *   data-i18n-placeholder="ui.message"   -> placeholder attribute
 *   data-i18n-title / -aria-label        -> those attributes
 */
export function applyTranslations(root = document) {
  for (const node of root.querySelectorAll('[data-i18n]')) {
    node.textContent = t(node.dataset.i18n);
  }
  const attributes = [
    ['i18nPlaceholder', 'placeholder'],
    ['i18nTitle', 'title'],
    ['i18nAriaLabel', 'aria-label'],
  ];
  for (const [dataKey, attribute] of attributes) {
    for (const node of root.querySelectorAll(`[data-${dataKey.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}]`)) {
      node.setAttribute(attribute, t(node.dataset[dataKey]));
    }
  }
  document.documentElement.lang = locale;
}

export default { t, has, getLocale, setLocale, onLocaleChange, applyTranslations, LOCALES, normalizeLocale };
